import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import {
  useGroup,
  useGroupMembers,
  useGroupPlays,
  useGroupSeasons,
  useGroupStandings,
  useMe,
} from '../api/hooks'
import type { GroupListItem, PlayListItem, SeasonRow, StandingsByGame } from '../api/types'
import { MembersTab } from '../components/MembersTab'
import { ScreenShell } from '../components/ScreenShell'
import { formatDayHeading, formatTimeShort, localDayKey } from '../datetime'
import {
  BackButton,
  GameThumb,
  MeepleAvatar,
  Pill,
  RatingBar,
  ScorePad,
  ScorePadRow,
  type Tab,
  Tabs,
  TierBadge,
  TopBar,
} from '../primitives'
import { formatSeasonDateRange, seasonStatusFor, winnerCalcLabel } from '../seasons/format'

type TabId = 'standings' | 'plays' | 'members' | 'seasons'

const TAB_DEFS: ReadonlyArray<Tab<TabId>> = [
  { id: 'standings', label: 'Standings' },
  { id: 'plays', label: 'Plays' },
  { id: 'members', label: 'Members' },
  { id: 'seasons', label: 'Seasons' },
]

export const GroupHomeScreen = () => {
  const { id } = useParams({ from: '/group/$id' })
  const navigate = useNavigate()
  const me = useMe()
  const group = useGroup(id)
  const members = useGroupMembers(id)
  const plays = useGroupPlays(id)
  const standings = useGroupStandings(id)
  const seasons = useGroupSeasons(id)
  const [tab, setTab] = useState<TabId>('standings')

  const tabsWithCounts: ReadonlyArray<Tab<TabId>> = TAB_DEFS.map((t) => ({
    ...t,
    count:
      t.id === 'plays'
        ? plays.data?.length
        : t.id === 'members'
          ? members.data?.length
          : t.id === 'standings'
            ? standings.data?.length
            : t.id === 'seasons'
              ? seasons.data?.length
              : undefined,
  }))

  return (
    <ScreenShell>
      <TopBar
        left={<BackButton fallback={() => navigate({ to: '/home' })} />}
        title={group.data?.name ?? 'Group'}
        sub={group.data?.description ?? null}
        right={
          <Link
            to="/group/$id/settings"
            params={{ id }}
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 12,
              color: 'var(--ink-2)',
              textDecoration: 'none',
              padding: '6px 10px',
              border: '1px solid var(--line-soft)',
              borderRadius: 4,
            }}
          >
            Settings
          </Link>
        }
      />

      <Tabs tabs={tabsWithCounts} active={tab} onChange={setTab} />

      <div style={{ padding: '14px 16px' }}>
        {tab === 'plays' && <PlaysTab plays={plays.data} loading={plays.isLoading} />}
        {tab === 'standings' && (
          <StandingsTab groupId={id} standings={standings.data} loading={standings.isLoading} />
        )}
        {tab === 'seasons' && (
          <SeasonsTab
            groupId={id}
            seasons={seasons.data}
            loading={seasons.isLoading}
            group={group.data}
          />
        )}
        {tab === 'members' && (
          <MembersTab
            groupId={id}
            members={members.data}
            loading={members.isLoading}
            viewerId={me.data?.id}
            viewerIsAdmin={group.data?.role === 'admin'}
          />
        )}
      </div>
    </ScreenShell>
  )
}

const PlaysTab = ({
  plays,
  loading,
}: {
  plays: PlayListItem[] | undefined
  loading: boolean
}) => {
  if (loading) return <div style={{ color: 'var(--ink-3)' }}>Loading…</div>
  if (!plays?.length) {
    return (
      <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
        <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 18 }}>
          Roll for initiative.
        </div>
        <div style={{ marginTop: 4, fontSize: 13 }}>
          No plays recorded yet. Tap above to record the first one.
        </div>
      </div>
    )
  }

  // Group plays by day in playedAt order (already desc).
  const groups = new Map<string, PlayListItem[]>()
  for (const row of plays) {
    const key = localDayKey(row.play.playedAt)
    const arr = groups.get(key) ?? []
    arr.push(row)
    groups.set(key, arr)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {Array.from(groups.entries()).map(([key, dayPlays]) => (
        <div key={key}>
          <div
            className="kicker"
            style={{ padding: '0 4px 6px', display: 'flex', justifyContent: 'space-between' }}
          >
            <span>{formatDayHeading(dayPlays[0]!.play.playedAt)}</span>
            <span>
              {dayPlays.length} play{dayPlays.length === 1 ? '' : 's'}
            </span>
          </div>
          <ScorePad>
            {dayPlays.map((row) => (
              <Link
                key={row.play.id}
                to="/play/$id"
                params={{ id: row.play.id }}
                style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
              >
                <ScorePadRow>
                  <GameThumb label={row.game.name} imageUrl={row.game.imageUrl} size={36} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>
                      {row.game.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      {formatTimeShort(row.play.playedAt)}
                      {row.play.location ? ` · ${row.play.location}` : ''}
                      {row.play.durationMinutes ? ` · ${row.play.durationMinutes}m` : ''}
                    </div>
                  </div>
                  <span style={{ color: 'var(--ink-3)' }}>→</span>
                </ScorePadRow>
              </Link>
            ))}
          </ScorePad>
        </div>
      ))}
    </div>
  )
}

const StandingsTab = ({
  groupId,
  standings,
  loading,
}: {
  groupId: string
  standings: StandingsByGame[] | undefined
  loading: boolean
}) => {
  if (loading) return <div style={{ color: 'var(--ink-3)' }}>Loading…</div>
  if (!standings?.length) {
    return (
      <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
        <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 18 }}>
          Nothing to rank yet.
        </div>
        <div style={{ marginTop: 4, fontSize: 13 }}>Record a play to start the standings.</div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {standings.map((s) => (
        <GameStandings key={s.game.id} groupId={groupId} standings={s} />
      ))}
    </div>
  )
}

// One game's leaderboard. Each row shows rank, player, an inline skill
// distribution bell on a per-game shared domain, and the tier/SR badge.
// The bell does the "how do these players compare, how confident are
// their ratings" work that used to live in a separate Field tab.
const GameStandings = ({
  groupId,
  standings,
}: {
  groupId: string
  standings: StandingsByGame
}) => {
  // Name column locked to a single width across the game so the bar
  // column (flex: 1) is identical pixel-width on every row. Without
  // this, longer names ("3 plays" vs "1 play", "Jules" vs "Cora")
  // steal pixels from the bar column row-to-row, putting the same μ
  // at different x positions.
  const maxNameLen = Math.max(...standings.entries.map((e) => e.displayName.length))
  const nameColWidth = Math.min(maxNameLen * 7 + 4, 90)

  return (
    <div>
      <Link
        to="/group/$id/game/$gameId"
        params={{ id: groupId, gameId: standings.game.id }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 4px 6px',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <GameThumb label={standings.game.name} imageUrl={standings.game.imageUrl} size={28} />
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>
          {standings.game.name}
        </div>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--terra)',
            lineHeight: 1,
          }}
          aria-hidden
        >
          →
        </span>
      </Link>
      <ScorePad>
        {standings.entries.map((e, i) => (
          <ScorePadRow key={e.userId} style={{ gap: 8 }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-3)',
                minWidth: 22,
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <MeepleAvatar name={e.displayName} hue={e.avatarHue} shape={e.avatarShape} size={28} />
            <div style={{ width: nameColWidth, flexShrink: 0 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {e.displayName}
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                {e.playCount} play{e.playCount === 1 ? '' : 's'}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <RatingBar mu={e.mu} sigma={e.sigma} width={160} height={14} />
            </div>
            <TierBadge tier={e.tier} score={e.mu} compact />
          </ScorePadRow>
        ))}
      </ScorePad>
    </div>
  )
}

// Per-game ridgeline of skill distributions across the group's players.
// Each player's bell sits on a shared X axis (auto-fitted to span all
// players in that game), so position-relative-to-the-pack and bell
// width (confidence) are both readable at a glance.
const SeasonsTab = ({
  groupId,
  seasons,
  loading,
  group,
}: {
  groupId: string
  seasons: SeasonRow[] | undefined
  loading: boolean
  group: GroupListItem | undefined
}) => {
  const isAdmin = group?.role === 'admin'

  if (loading) return <div style={{ color: 'var(--ink-3)' }}>Loading…</div>
  if (!seasons?.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 18 }}>
            No seasons yet.
          </div>
          <div style={{ marginTop: 4, fontSize: 13 }}>
            Seasons run a leaderboard across a date range.
            {isAdmin ? '' : ' Ask an admin to start one.'}
          </div>
        </div>
        {isAdmin && (
          <Link
            to="/group/$id/seasons/new"
            params={{ id: groupId }}
            className="btn-stamp"
            style={{ textAlign: 'center', textDecoration: 'none' }}
          >
            + New season
          </Link>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <ScorePad>
        {seasons.map((s) => {
          const status = seasonStatusFor(s)
          return (
            <ScorePadRow key={s.id} style={{ alignItems: 'flex-start', padding: '12px 14px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link
                  to="/season/$id"
                  params={{ id: s.id }}
                  style={{
                    display: 'block',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    fontSize: 15,
                    color: 'var(--ink)',
                    textDecoration: 'none',
                  }}
                >
                  {s.name}
                </Link>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  {formatSeasonDateRange(s.startAt, s.endAt)} ·{' '}
                  {winnerCalcLabel(s.winnerCalcMethod)}
                </div>
              </div>
              <Pill
                color={status === 'active' ? 'felt' : status === 'scheduled' ? 'gilt' : 'slate'}
              >
                {status}
              </Pill>
            </ScorePadRow>
          )
        })}
      </ScorePad>
      {isAdmin && (
        <Link
          to="/group/$id/seasons/new"
          params={{ id: groupId }}
          className="btn-stamp"
          style={{ textAlign: 'center', textDecoration: 'none' }}
        >
          + New season
        </Link>
      )}
    </div>
  )
}
